import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { STATUS_DASHBOARD_CACHED_RESPONSE_KEY, STATUS_DASHBOARD_ETAG_KEY } from '@shared/constants';
import {
    ICalendarData,
    IReportRequest,
    IReportRunResponse,
    IReportResponse,
    IConfigResponse,
    IFolderNode,
    IReportEditRequest,
    IFolderCreateRequest,
    IFolderEditRequest,
    ITemplate,
    ITemplateStageSection,
    ITemplateStage,
    TemplateStageType,
    IReportSharedUser,
    IReportRunDownloadLink,
    ICalendarRequest,
    IEditNodeRequest,
    IReportEditFolderRequest,
    IReportEditNextRunOnRequest,
    IRunSqlResponse,
    IUser,
    IUsersResponse,
    IUserExistsResponse,
    UploadMetadataType,
    ILastReportRunResponse,
    IStatusDashboardResponse,
    IUserActivityResponse,
    IReportDetail,
    ConfigKey,
    IConfigRequest,
    ITableRelsResponse,
    ITableRel,
    ITableSourceOptionsResponse,
    IFactDetailsResponse,
    IFactDetails,
    ISqlViews,
    ISqlViewsResponse,
    ISections,
    ISectionsResponse,
    ISectionNode,
    ISectionNodesResponse,
    ITemplates,
    ITemplatesResponse,
    ITemplateSection,
    IAdhocReport,
    IAdhocShare,
    IPredefinedReport,
    IFileUploadRequest,
    IFileUploadResponse,
    IFileUploadCompleteRequest,
    IChunkUploadRequest,
    IChunkUploadResponse,
    IChunkCompleteRequest,
} from '@shared/models';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { IStage, ITemplateDetail, ITemplateResponse, ITemplateStageSectionNode, TemplateMetadata } from '../models';
import { ConfigService } from './config.service';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    constructor(
        private httpClient: HttpClient,
        private configService: ConfigService,
    ) {}

    get apiUrl(): string {
        return this.configService.apiEndpoint;
    }

    httpHeader = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        }),
    };

    //
    // CALENDAR
    //
    fetchCalendar(request: ICalendarRequest): Observable<ICalendarData[]> {
        return this.httpClient.get<ICalendarData[]>(`${this.apiUrl}/calendar`, { params: { ...request } }).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // CONFIG
    //

    fetchConfig(): Observable<IConfigResponse[]> {
        return this.httpClient.get<IConfigResponse[]>(`${this.apiUrl}/config`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    updateConfig(configKey: ConfigKey, payload: IConfigRequest): Observable<IConfigResponse> {
        return this.httpClient.post<IConfigResponse>(`${this.apiUrl}/manage/config/${configKey}`, payload).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // FOLDERS
    //

    deleteFolder(folderId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/folders/${folderId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    createFolder(request: IFolderCreateRequest): Observable<IFolderNode> {
        return this.httpClient.post<IFolderNode>(`${this.apiUrl}/folders`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editFolder(folderId: number, request: IFolderEditRequest): Observable<IFolderNode> {
        return this.httpClient.patch<IFolderNode>(`${this.apiUrl}/folders/${folderId}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchFolders(): Observable<IFolderNode> {
        return this.httpClient.get<IFolderNode>(`${this.apiUrl}/folders`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // REPORTS
    //

    createReport(request: IReportRequest): Observable<IReportResponse> {
        return this.httpClient.post<IReportResponse>(`${this.apiUrl}/reports`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchReports(): Observable<IReportResponse[]> {
        return this.httpClient.get<IReportResponse[]>(`${this.apiUrl}/reports`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchReport(reportId: number): Observable<IReportResponse> {
        return this.httpClient.get<IReportResponse>(`${this.apiUrl}/reports/${reportId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchReportSharedUsers(reportId: number): Observable<IReportSharedUser[]> {
        return this.httpClient.get<IReportSharedUser[]>(`${this.apiUrl}/reports/${reportId}/sharedWith`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    shareReport(reportId: number, request: IReportSharedUser): Observable<IReportSharedUser> {
        return this.httpClient.post<IReportSharedUser>(`${this.apiUrl}/reports/${reportId}/share`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editReport(reportId: number, request: IReportEditRequest): Observable<IReportResponse> {
        return this.httpClient.put<IReportResponse>(`${this.apiUrl}/reports/${reportId}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editReportFolder(reportId: number, request: IReportEditFolderRequest): Observable<IReportResponse> {
        return this.httpClient.post<IReportResponse>(`${this.apiUrl}/reports/${reportId}/folderId`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editReportNextRunOn(reportId: number, request: IReportEditNextRunOnRequest): Observable<IReportResponse> {
        return this.httpClient.post<IReportResponse>(`${this.apiUrl}/reports/${reportId}/nextRunOn`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    runNowReport(reportId: number): Observable<IReportRunResponse> {
        return this.httpClient.post<IReportRunResponse>(`${this.apiUrl}/reports/${reportId}/runNow`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchLastReportRuns(): Observable<ILastReportRunResponse> {
        return this.httpClient.get<ILastReportRunResponse>(`${this.apiUrl}/lastRuns`);
    }

    deleteReportRun(reportId: number, runId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/runs/${runId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteReport(reportId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/reports/${reportId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchReportRuns(reportId: number): Observable<IReportRunResponse[]> {
        return this.httpClient.get<IReportRunResponse[]>(`${this.apiUrl}/reports${reportId}/runs`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // RUNS
    //

    fetchRuns(): Observable<IReportRunResponse[]> {
        return this.httpClient.get<IReportRunResponse[]>(`${this.apiUrl}/runs`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchRunFileDownloadLink(runId: number, outputId: number): Observable<IReportRunDownloadLink> {
        return this.httpClient
            .get<IReportRunDownloadLink>(`${this.apiUrl}/runs/${runId}/outputFiles/${outputId}/downloadLink`)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchRunFileDownload(runId: number, outputId: number): Observable<HttpResponse<Blob>> {
        return this.httpClient
            .get(`${this.apiUrl}/runs/${runId}/outputFiles/${outputId}/download`, { responseType: 'blob', observe: 'response' })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchExcelDownload(dataType: string): Observable<HttpResponse<Blob>> {
        return this.httpClient
            .get(`${this.apiUrl}/manage/download-section-nodes/${dataType}`, { responseType: 'blob', observe: 'response' })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }
    //
    // STAGES
    //
    fetchActiveStages(): Observable<IStage[]> {
        return this.httpClient.get<IStage[]>(`${this.apiUrl}/manage/active-stages`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // SECTIONS
    //
    fetchActiveSections(templateDataType: string): Observable<ITemplateStageSection[]> {
        return this.httpClient
            .get<ITemplateStageSection[]>(`${this.apiUrl}/manage/active-sections/${templateDataType}`)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // SECTION NODES
    //

    fetchSectionsNodes(templateId: number | null, sectionId: number): Observable<ITemplateStageSectionNode[]> {
        let params = new HttpParams().set('sectionId', sectionId.toString());
        if (templateId != null) {
            params = params.set('templateId', templateId.toString());
        }
        return this.httpClient
            .get<ITemplateStageSectionNode[]>(`${this.apiUrl}/manage/template-wizard-nodes`, { params })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    //
    // TEMPLATES
    //

    fetchTemplateById(id: number): Observable<TemplateMetadata> {
        return this.httpClient.get<TemplateMetadata>(`${this.apiUrl}/manage/template-wizard/${id}`).pipe(
            retry(1),
            catchError(() => {
                return throwError(() => new Error('Template not found'));
            }),
        );
    }

    createTemplate(request: ITemplateDetail): Observable<ITemplateResponse> {
        return this.httpClient.post<ITemplateResponse>(`${this.apiUrl}/manage/template-wizard`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    updateTemplate(id: number, request: ITemplateDetail): Observable<ITemplateResponse> {
        return this.httpClient.put<ITemplateResponse>(`${this.apiUrl}/manage/template-wizard/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTemplates(): Observable<ITemplate[]> {
        return this.httpClient.get<ITemplate[]>(`${this.apiUrl}/templates`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTemplateSections(templateId: number, stageType: TemplateStageType): Observable<ITemplateStageSection[]> {
        return this.httpClient
            .get<ITemplateStageSection[]>(`${this.apiUrl}/templates/${templateId}/stages/${stageType}`)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTemplateAllSections(templateId: number): Observable<ITemplateSection[]> {
        return this.httpClient.get<ITemplateSection[]>(`${this.apiUrl}/templates/${templateId}/sections`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSectionsByDataType(dataType: string): Observable<ISections[]> {
        return this.httpClient.get<ISections[]>(`${this.apiUrl}/manage/sections/datatype/${dataType}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTemplateStages(templateId: number): Observable<ITemplateStage[]> {
        return this.httpClient.get<ITemplateStage[]>(`${this.apiUrl}/templates/${templateId}/stages`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editNode(nodeId: number, request: IEditNodeRequest): Observable<IEditNodeRequest> {
        return this.httpClient.put<IEditNodeRequest>(`${this.apiUrl}/manage/nodes/${nodeId}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchUsers(): Observable<IUsersResponse> {
        return this.httpClient.get<IUsersResponse>(`${this.apiUrl}/manage/users`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTableRelations(): Observable<ITableRelsResponse> {
        return this.httpClient.get<ITableRelsResponse>(`${this.apiUrl}/manage/relationships`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTableRelation(relationId: number): Observable<ITableRel> {
        return this.httpClient.get<ITableRel>(`${this.apiUrl}/manage/relationships/${relationId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }
    fetchFactDetail(recordId: number): Observable<IFactDetails> {
        return this.httpClient.get<IFactDetails>(`${this.apiUrl}/manage/facts/${recordId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTableSourceOptions(): Observable<ITableSourceOptionsResponse> {
        return this.httpClient.get<ITableSourceOptionsResponse>(`${this.apiUrl}/manage/table-source-options`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchViewNames(): Observable<ITableSourceOptionsResponse> {
        return this.httpClient.get<ITableSourceOptionsResponse>(`${this.apiUrl}/manage/view-names`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchFactDetails(): Observable<IFactDetailsResponse> {
        return this.httpClient.get<IFactDetailsResponse>(`${this.apiUrl}/manage/facts`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    checkUserExists(email: string): Observable<IUserExistsResponse> {
        return this.httpClient
            .get<IUserExistsResponse>(`${this.apiUrl}/users/exists`, {
                params: { email },
            })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchMe(): Observable<IUser> {
        return this.httpClient.get<IUser>(`${this.apiUrl}/me`).pipe(retry(1));
    }

    addUserLogin(): void {
        this.httpClient.post(`${this.apiUrl}/log/login`, {}).pipe(retry(1), catchError(this.httpErrorHandler)).subscribe();
    }

    editUser(id: number, request: IUser): Observable<IUser> {
        return this.httpClient.put<IUser>(`${this.apiUrl}/manage/users/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editTableRel(id: number, request: ITableRel): Observable<ITableRel> {
        return this.httpClient.put<ITableRel>(`${this.apiUrl}/manage/relationships/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addTableRel(request: ITableRel): Observable<ITableRel> {
        return this.httpClient.post<ITableRel>(`${this.apiUrl}/manage/relationships`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editFactDetail(id: number, request: IFactDetails): Observable<IFactDetails> {
        return this.httpClient.put<IFactDetails>(`${this.apiUrl}/manage/facts/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addFactDetail(request: IFactDetails): Observable<IFactDetails> {
        return this.httpClient.post<IFactDetails>(`${this.apiUrl}/manage/facts`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteUser(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/users/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteTableRelation(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/relationships/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteFactDetail(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/facts/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    runSqlQuery(sqlQuery: string, asCsv: boolean): Observable<IRunSqlResponse | Blob> {
        return asCsv
            ? this.httpClient.post(`${this.apiUrl}/manage/runsql`, sqlQuery, { params: { rawResult: 1 }, responseType: 'blob' }).pipe(
                  catchError((errorResponse: HttpErrorResponse) => {
                      return from(errorResponse.error.text() as Promise<string>).pipe(
                          map((errorResponse) => {
                              throw JSON.parse(errorResponse);
                          }),
                      );
                  }),
              )
            : this.httpClient.post<IRunSqlResponse>(`${this.apiUrl}/manage/runsql`, sqlQuery);
    }

    uploadMetadata(type: UploadMetadataType, file: File): Observable<void> {
        const formData = new FormData();
        formData.append('file', file);
        return this.httpClient.post<void>(`${this.apiUrl}/manage/uploadmetadata/${type}`, formData).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchRunDetails(runId: number): Observable<IReportDetail> {
        // temp fix
        // return this.httpClient.get<IReportDetail>(`${this.apiUrl}/manage/runDetails/${runId}`)
        return this.httpClient.get<IReportDetail>(`${this.apiUrl}/manage/statusDashboardDetails/${runId}`).pipe(catchError(this.httpErrorHandler));
    }

    fetchStatusDashboard(date: string): Observable<IStatusDashboardResponse> {
        const savedETagKeyData = localStorage.getItem(STATUS_DASHBOARD_ETAG_KEY);
        const headers = new HttpHeaders();

        if (savedETagKeyData) {
            // Disable caching for now - causing issues,
            // headers = headers.set('If-None-Match', savedETagKeyData)
        }

        return this.httpClient.get<IStatusDashboardResponse>(`${this.apiUrl}/manage/statusDashboard`, { headers, observe: 'response', params: { date } }).pipe(
            map((response) => {
                if (response.status === 304) {
                    const cachedResponseData = localStorage.getItem(STATUS_DASHBOARD_CACHED_RESPONSE_KEY);
                    if (!cachedResponseData) {
                        throw new Error('Expected cache entry not found');
                    }
                    return JSON.parse(cachedResponseData) as IStatusDashboardResponse;
                } else {
                    const newETagHeader = response.headers.get('ETag');
                    const bodyStr = JSON.stringify(response.body);

                    if (bodyStr.length < 5000000 && newETagHeader) {
                        // Limit 5 MB for response
                        localStorage.setItem(STATUS_DASHBOARD_ETAG_KEY, newETagHeader);
                        localStorage.setItem(STATUS_DASHBOARD_CACHED_RESPONSE_KEY, bodyStr);
                    }

                    return response.body as IStatusDashboardResponse;
                }
            }),
            catchError(this.httpErrorHandler),
        );
    }

    fetchUserActivities(date: string): Observable<IUserActivityResponse> {
        return this.httpClient
            .get<IUserActivityResponse>(`${this.apiUrl}/manage/usersActivity`, { params: { date } })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchAdhocReports(): Observable<IAdhocReport[]> {
        return this.httpClient.get<IAdhocReport[]>(`${this.apiUrl}/adhoc_reports`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchPredefinedReports(): Observable<IPredefinedReport[]> {
        return this.httpClient.get<IPredefinedReport[]>(`${this.apiUrl}/predefined-reports`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    uploadAdhocReport(file: File, title: string, description?: string, sharedWithEmails?: string[]): Observable<IAdhocReport> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title);
        formData.append('description', description ?? '');

        if (sharedWithEmails) {
            formData.append('sharedWithEmails', JSON.stringify(sharedWithEmails));
        }

        return this.httpClient.post<IAdhocReport>(`${this.apiUrl}/adhoc_reports/upload`, formData).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    getUploadUrl(fileName: string, fileSize: number, title: string, description?: string, sharedWithEmails?: string[]): Observable<IFileUploadResponse> {
        const request: IFileUploadRequest = {
            fileName,
            fileSize,
            title,
            description,
            sharedWithEmails,
        };

        return this.httpClient
            .post<IFileUploadResponse>(`${this.apiUrl}/adhoc_reports/sas-upload-url`, request)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    uploadToBlobStorage(sasUrl: string, file: File): Observable<any> {
        const headers = new HttpHeaders({
            'x-ms-blob-type': 'BlockBlob',
        });

        return this.httpClient.put(sasUrl, file, {
            headers,
            observe: 'response',
            reportProgress: true,
        });
    }

    completeBlobUpload(reportId: number): Observable<IAdhocReport> {
        const request: IFileUploadCompleteRequest = { reportId };

        return this.httpClient
            .post<IAdhocReport>(`${this.apiUrl}/adhoc_reports/sas-upload-complete`, request)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    updateAdhocReport(reportId: number, title: string, description?: string): Observable<IAdhocReport> {
        const payload: { title: string; description?: string } = { title };
        if (description !== undefined) {
            payload.description = description;
        }

        return this.httpClient.put<IAdhocReport>(`${this.apiUrl}/adhoc_reports/${reportId}`, payload).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchAdhocReportShares(reportId: number): Observable<IAdhocShare[]> {
        return this.httpClient.get<IAdhocShare[]>(`${this.apiUrl}/adhoc_reports/${reportId}/shares`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    shareAdhocReport(reportId: number, email: string): Observable<IAdhocShare> {
        return this.httpClient
            .post<IAdhocShare>(`${this.apiUrl}/adhoc_reports/${reportId}/shares`, { email })
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteAdhocReportShare(reportId: number, shareId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/adhoc_reports/${reportId}/shares/${shareId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteAdhocReport(reportId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/adhoc_reports/${reportId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    downloadAdhocReport(id: number): Observable<HttpResponse<Blob>> {
        return this.httpClient
            .get(`${this.apiUrl}/adhoc_reports/${id}/download`, { responseType: 'blob', observe: 'response' })
            .pipe(catchError(this.httpErrorHandler));
    }

    preheatDatamart(): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/manage/preheatdatamart`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    syncVendors(): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/manage/syncvendors`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    syncAll(): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/manage/sync`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    syncSuppliers(): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/manage/syncsuppliers`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    syncTable(tableName: string): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/manage/sync/${tableName}`, {}).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    // Chunk-based upload methods
    initiateChunkUpload(
        fileName: string,
        fileSize: number,
        fileType: string,
        title: string,
        description?: string,
        sharedWithEmails?: string[],
    ): Observable<IChunkUploadResponse> {
        const request: IChunkUploadRequest = { fileName, fileSize, fileType, title, description, sharedWithEmails };

        return this.httpClient
            .post<IChunkUploadResponse>(`${this.apiUrl}/adhoc_reports/chunk-upload/initiate`, request)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    uploadChunk(uploadId: string, chunk: Blob, chunkIndex: number): Observable<void> {
        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('chunkIndex', chunkIndex.toString());

        return this.httpClient
            .post<void>(`${this.apiUrl}/adhoc_reports/chunk-upload/${uploadId}/chunk/${chunkIndex}`, formData)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    completeChunkUpload(uploadId: string, totalChunks: number): Observable<IAdhocReport> {
        const request: IChunkCompleteRequest = {
            uploadId,
            totalChunks,
        };

        return this.httpClient
            .post<IAdhocReport>(`${this.apiUrl}/adhoc_reports/chunk-upload/${uploadId}/complete`, request)
            .pipe(retry(1), catchError(this.httpErrorHandler));
    }

    httpErrorHandler = (error: HttpErrorResponse) => {
        let msg = '';
        if (error.error instanceof ErrorEvent) {
            // client side error
            msg = error.error.message;
        } else {
            // server side error
            msg = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        return throwError(() => msg);
    };

    deleteSqlView(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/views/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editSqlView(id: string, request: ISqlViews): Observable<ISqlViews> {
        return this.httpClient.put<ISqlViews>(`${this.apiUrl}/manage/views/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addSqlView(request: ISqlViews): Observable<ISqlViews> {
        return this.httpClient.post<ISqlViews>(`${this.apiUrl}/manage/views`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSqlViews(): Observable<ISqlViewsResponse> {
        return this.httpClient.get<ISqlViewsResponse>(`${this.apiUrl}/manage/views`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSqlView(recordId: string): Observable<ISqlViews> {
        return this.httpClient.get<ISqlViews>(`${this.apiUrl}/manage/views/${recordId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteSections(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/sections/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editSections(id: number, request: ISections): Observable<ISections> {
        return this.httpClient.put<ISections>(`${this.apiUrl}/manage/sections/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addSections(request: ISections): Observable<ISections> {
        return this.httpClient.post<ISections>(`${this.apiUrl}/manage/sections`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSections(): Observable<ISectionsResponse> {
        return this.httpClient.get<ISectionsResponse>(`${this.apiUrl}/manage/sections`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSection(recordId: number): Observable<ISections> {
        return this.httpClient.get<ISections>(`${this.apiUrl}/manage/sections/${recordId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    // Section Nodes
    fetchSectionNodes(sectionId?: number, stageType?: TemplateStageType, topLevelItemId?: number): Observable<ISectionNodesResponse> {
        const params: { [key: string]: string } = {};
        if (sectionId) {
            params['sectionId'] = sectionId.toString();
        }
        if (stageType) {
            params['stageType'] = stageType;
        }
        if (topLevelItemId) {
            params['topLevelItemId'] = topLevelItemId.toString();
        }
        return this.httpClient.get<ISectionNodesResponse>(`${this.apiUrl}/manage/section-nodes`, { params }).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchRootSectionNodes(sectionId: number, stageType: TemplateStageType): Observable<ISectionNodesResponse> {
        const params: { [key: string]: string } = {};
        if (sectionId) {
            params['sectionId'] = sectionId.toString();
        }
        if (stageType) {
            params['stageType'] = stageType;
        }
        return this.httpClient.get<ISectionNodesResponse>(`${this.apiUrl}/manage/top-nodes`, { params }).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchSectionNode(recordId: number): Observable<ISectionNode> {
        return this.httpClient.get<ISectionNode>(`${this.apiUrl}/manage/section-nodes/${recordId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    canDeleteSectionNode(id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.apiUrl}/manage/section-nodes/${id}/candelete`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addSectionNode(request: ISectionNode): Observable<ISectionNode> {
        return this.httpClient.post<ISectionNode>(`${this.apiUrl}/manage/section-nodes`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editSectionNode(id: number, request: ISectionNode): Observable<ISectionNode> {
        return this.httpClient.put<ISectionNode>(`${this.apiUrl}/manage/section-nodes/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteSectionNode(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/section-nodes/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    deleteTemplates(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/manage/templates/${id}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    editTemplates(id: number, request: ITemplates): Observable<ITemplates> {
        return this.httpClient.put<ITemplates>(`${this.apiUrl}/manage/templates/${id}`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    addTemplates(request: ITemplates): Observable<ITemplates> {
        return this.httpClient.post<ITemplates>(`${this.apiUrl}/manage/templates`, request).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchManageTemplates(): Observable<ITemplatesResponse> {
        return this.httpClient.get<ITemplatesResponse>(`${this.apiUrl}/manage/templates`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    fetchTemplate(recordId: number): Observable<ITemplates> {
        return this.httpClient.get<ITemplates>(`${this.apiUrl}/manage/templates/${recordId}`).pipe(retry(1), catchError(this.httpErrorHandler));
    }

    uploadSectionNodes(file: File, templateDataType: string): Observable<void> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('templateDataType', templateDataType);
        return this.httpClient.post<void>(`${this.apiUrl}/manage/upload-section-nodes`, formData).pipe(retry(1), catchError(this.httpErrorHandler));
    }
}
